import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import PageTitle from "./PageTitle";
import { titleForPath } from "@/lib/pageTitles";
import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  Map,
  KanbanSquare,
  Sparkles,
  CheckSquare,
  Settings,
  LogOut,
  Star,
  Megaphone,
  Users as AudienceIcon,
  MousePointer,
  Send,
  Phone,
  Share2,
  Lock,
  Shield,
  Building2,
  ArrowLeftCircle,
  Puzzle,
  MailCheck,
  Menu,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { resetPostHogIdentity } from "../lib/posthog";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import NotificationBell from "./NotificationBell";
import PwaInstallPrompt from "./PwaInstallPrompt";

const PRIMARY_NAV = [
  { to: "/app", end: true, label: "Overview", icon: LayoutDashboard, id: "nav-overview" },
  { to: "/app/clients", label: "Clients", icon: Users, id: "nav-clients" },
  { to: "/app/trips", label: "Trips", icon: Map, id: "nav-trips" },
  { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare, id: "nav-pipeline" },
  { to: "/app/ai", label: "Polaris Copilot", icon: Sparkles, id: "nav-ai" },
  { to: "/app/tasks", label: "Tasks", icon: CheckSquare, id: "nav-tasks" },
];

const MARKETING_NAV = [
  { to: "/app/marketing", end: true, label: "Overview", icon: Megaphone, id: "nav-marketing" },
  { to: "/app/marketing/campaigns", label: "Campaigns", icon: Send, id: "nav-campaigns" },
  { to: "/app/marketing/segments", label: "Audiences", icon: AudienceIcon, id: "nav-segments" },
  { to: "/app/marketing/popups", label: "Lead forms", icon: MousePointer, id: "nav-popups" },
  { to: "/app/marketing/leads", label: "Leads", icon: Send, id: "nav-leads" },
  { to: "/app/marketing/calls", label: "Calls", icon: Phone, id: "nav-calls" },
  { to: "/app/marketing/social", label: "Social", icon: Share2, id: "nav-social" },
];

const FOOTER_NAV = [
  { to: "/app/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const plan = user?.plan || "starter";
  const isPro = plan === "pro" || plan === "agency";
  const isSuperAdmin = user?.is_superadmin === true;
  const isWorkspaceOwner = user?.workspace_role === "owner";
  const marketingEnabled = user?.marketing_enabled === true;

  const [returnToken] = useState(() => localStorage.getItem("tcrm_return_token"));
  const [returnName] = useState(() => localStorage.getItem("tcrm_return_name") || "your account");

  const exitImpersonation = () => {
    const tok = localStorage.getItem("tcrm_return_token");
    if (tok) {
      resetPostHogIdentity();
      localStorage.setItem("tcrm_token", tok);
      localStorage.removeItem("tcrm_return_token");
      localStorage.removeItem("tcrm_return_name");
      localStorage.removeItem("tcrm_user");
      window.location.href = "/app/agency-admin";
    }
  };

  const resendVerification = async () => {
    setVerificationBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email");
    } finally {
      setVerificationBusy(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.full_name || "T A")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const topOffsetClass = returnToken || user?.email_verified === false ? "pt-10" : "";
  const sidebarTopClass = returnToken || user?.email_verified === false ? "top-10" : "top-0";

  return (
    <div className="min-h-screen bg-[#FAF6EE] text-[#2B2320]">
      <PageTitle title={titleForPath(pathname)} />
      {returnToken && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm text-white shadow-md">
          <span className="font-medium">Viewing as {user?.full_name || "agent"}</span>
          <span className="opacity-70">-</span>
          <button
            onClick={exitImpersonation}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 font-medium transition hover:bg-white/30"
          >
            <ArrowLeftCircle className="w-3.5 h-3.5" />
            Return to {returnName}
          </button>
        </div>
      )}

      {user?.email_verified === false && !returnToken && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 bg-[#2B2320] px-4 py-2 text-sm text-white shadow-md">
          <MailCheck className="w-4 h-4 text-[#F4C9B8]" />
          <span>Verify your email to keep password resets and account alerts secure.</span>
          <button
            onClick={resendVerification}
            disabled={verificationBusy}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-medium transition hover:bg-white/25 disabled:opacity-60"
          >
            {verificationBusy ? "Sending..." : "Resend verification"}
          </button>
        </div>
      )}

      <aside
        data-testid="app-sidebar"
        className={`fixed left-0 hidden h-screen w-64 border-r border-[#E8DFD1] bg-white lg:flex lg:flex-col ${sidebarTopClass}`}
      >
        <SidebarContent
          user={user}
          initials={initials}
          isPro={isPro}
          isSuperAdmin={isSuperAdmin}
          isWorkspaceOwner={isWorkspaceOwner}
          marketingEnabled={marketingEnabled}
          onLogout={handleLogout}
        />
      </aside>

      <div className={`sticky top-0 z-40 border-b border-[#E8DFD1] bg-[#FAF6EE]/95 backdrop-blur lg:hidden ${topOffsetClass} mobile-safe-top`}>
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#2B2320] text-white">
              <Star className="w-4 h-4 fill-white" strokeWidth={0} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-body-lg font-semibold tracking-tight leading-none">Polaris</div>
              <div className="mt-1 truncate text-caption uppercase tracking-[0.22em] text-[#A69C92]">
                {user?.company || "Travel CRM"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#E8DFD1] bg-white text-[#2B2320]"
                  aria-label="Open navigation"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto border-r border-[#E8DFD1] bg-[#FAF6EE] p-0">
                <SheetHeader className="border-b border-[#E8DFD1] px-4 py-4">
                  <div className="flex items-center gap-2.5 pr-10">
                    <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#2B2320] text-white">
                      <Star className="w-4 h-4 fill-white" strokeWidth={0} />
                    </div>
                    <div>
                      <SheetTitle className="font-display text-left text-body-lg font-semibold tracking-tight">Polaris</SheetTitle>
                      <div className="mt-1 text-caption uppercase tracking-[0.22em] text-[#A69C92]">Travel CRM</div>
                    </div>
                  </div>
                </SheetHeader>

                <SidebarContent
                  user={user}
                  initials={initials}
                  isPro={isPro}
                  isSuperAdmin={isSuperAdmin}
                  isWorkspaceOwner={isWorkspaceOwner}
                  marketingEnabled={marketingEnabled}
                  onLogout={handleLogout}
                  onNavigate={() => setMobileNavOpen(false)}
                  mobile
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <main className={`min-h-screen lg:pl-64 ${topOffsetClass}`}>
        <Outlet />
      </main>

      <PwaInstallPrompt />
    </div>
  );
}

function SidebarContent({
  user,
  initials,
  isPro,
  isSuperAdmin,
  isWorkspaceOwner,
  marketingEnabled,
  onLogout,
  onNavigate,
  mobile = false,
}) {
  return (
    <>
      <div className="flex items-center justify-between px-6 pt-7 pb-5">
        <div className="flex items-center gap-2.5" data-testid="app-brand-mark">
          <div className="relative grid h-9 w-9 place-items-center rounded-xl bg-[#2B2320] text-white">
            <Star className="w-4 h-4 fill-white" strokeWidth={0} />
            <span className="absolute inset-0 rounded-xl ring-1 ring-white/5" />
          </div>
          <div>
            <div className="font-display text-body-lg font-semibold tracking-tight leading-none">Polaris</div>
            <div className="mt-1 text-caption uppercase tracking-[0.22em] text-[#A69C92]">Travel CRM</div>
          </div>
        </div>
        {!mobile && <NotificationBell />}
      </div>

      <nav className="mt-3 flex-1 overflow-y-auto px-3">
        <NavSection title="Workspace" items={PRIMARY_NAV} isPro={isPro} onNavigate={onNavigate} />
        {marketingEnabled && (
          <NavSection title="Marketing" items={MARKETING_NAV} isPro={isPro} className="mt-6" onNavigate={onNavigate} />
        )}
        <div className="mt-6">
          <ul className="space-y-0.5">
            {FOOTER_NAV.map((item) => <NavItem key={item.to} item={item} isPro={isPro} onNavigate={onNavigate} />)}
            {isWorkspaceOwner && (
              <li>
                <NavLink
                  to="/app/agency-admin"
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive ? "bg-[#2B2320] text-white shadow-sm" : "text-[#6B6158] hover:bg-[#F2EADB] hover:text-[#2B2320]"
                    }`
                  }
                >
                  <Building2 className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  <span className="flex-1">Agency Admin</span>
                </NavLink>
              </li>
            )}
            {isSuperAdmin && (
              <li>
                <NavLink
                  to="/admin"
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive ? "bg-amber-500/15 text-amber-600" : "text-[#A69C92] hover:bg-amber-500/10 hover:text-amber-600"
                    }`
                  }
                >
                  <Shield className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  <span className="flex-1">Admin Panel</span>
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </nav>

      <div className="mx-3 mb-2">
        <a
          href="/extension"
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs transition-all hover:opacity-90"
          style={{ background: "#F2EADB", border: "1px solid #E8DFD1" }}
        >
          <Puzzle className="w-3.5 h-3.5 shrink-0 text-[#6B6158]" strokeWidth={1.75} />
          <div className="min-w-0 flex-1">
            <div className="font-medium leading-none text-[#2B2320]">Chrome Extension</div>
            <div className="mt-0.5 truncate text-caption text-[#A69C92]">Import bookings in one click</div>
          </div>
        </a>
      </div>

      {!isPro && (
        <div className="mx-3 mb-3">
          <NavLink
            to="/app/settings?section=plan"
            onClick={onNavigate}
            className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs transition-all"
            style={{ background: "#FFF4EE", border: "1px solid #F4C9B8" }}
          >
            <Sparkles className="w-3.5 h-3.5 shrink-0 text-[#C96F53]" />
            <div className="min-w-0 flex-1">
              <div className="font-medium leading-none text-[#C96F53]">Free plan</div>
              <div className="mt-0.5 truncate text-caption text-[#A69C92]">Upgrade to unlock Pro</div>
            </div>
            <Lock className="h-3 w-3 shrink-0 text-[#C96F53]" />
          </NavLink>
        </div>
      )}

      <div className={`flex items-center gap-3 border-t border-[#E8DFD1] p-4 ${mobile ? "mobile-safe-bottom" : ""}`}>
        <Avatar className="w-9 h-9">
          <AvatarFallback className="bg-[#2B2320] text-xs font-medium text-white">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-[#2B2320]" data-testid="sidebar-user-name">{user?.full_name}</div>
          <div className="truncate text-xs text-[#8B827A]">{user?.company || user?.email}</div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="rounded-lg p-2 text-[#8B827A] transition hover:bg-[#F2EADB] hover:text-[#2B2320]"
          aria-label="Sign out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

function NavSection({ title, items, isPro, className = "", onNavigate }) {
  return (
    <div className={className}>
      <div className="mb-2 px-3 text-caption uppercase tracking-[0.22em] text-[#A69C92]">{title}</div>
      <ul className="space-y-0.5">
        {items.map((item) => <NavItem key={item.to} item={item} isPro={isPro} onNavigate={onNavigate} />)}
      </ul>
    </div>
  );
}

function NavItem({ item, isPro, onNavigate }) {
  const locked = item.pro && !isPro;

  return (
    <li>
      <NavLink
        end={item.end}
        to={item.to}
        onClick={onNavigate}
        data-testid={item.id}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
            isActive
              ? "bg-[#2B2320] text-white shadow-sm"
              : locked
                ? "text-[#A69C92] hover:bg-[#F2EADB] hover:text-[#6B6158]"
                : "text-[#6B6158] hover:bg-[#F2EADB] hover:text-[#2B2320]"
          }`
        }
      >
        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
        <span className="flex-1">{item.label}</span>
        {locked && <Lock className="w-3 h-3 shrink-0 text-[#C96F53]" strokeWidth={2} />}
      </NavLink>
    </li>
  );
}
